import { PermissionState, PluginListenerHandle, registerPlugin } from '@capacitor/core';

export interface CallKitPlugin {
  debugLog(options: CallKitDebugLogOptions): Promise<void>;

  fulfilled(): Promise<void>;

  requestPhonePermission(): Promise<void>;

  requestXiaomiPermission(): Promise<void>;

  checkOngoingCallNotificationPermission(): Promise<OngoingCallNotificationPermissionStatus>;

  requestOngoingCallNotificationPermission(): Promise<OngoingCallNotificationPermissionStatus>;

  activateBackgroundCallMode(options: BackgroundCallModeOptions): Promise<void>;

  deactivateBackgroundCallMode(): Promise<void>;

  activateVideoCallPictureInPicture(options: VideoCallPictureInPictureOptions): Promise<VideoCallPictureInPictureStatus>;

  deactivateVideoCallPictureInPicture(
    options?: VideoCallPictureInPictureDeactivateOptions,
  ): Promise<VideoCallPictureInPictureStatus>;

  activateExecutorVideoCallPictureInPicture(
    options: ExecutorVideoCallPictureInPictureOptions,
  ): Promise<VideoCallPictureInPictureStatus>;

  deactivateExecutorVideoCallPictureInPicture(
    options?: VideoCallPictureInPictureDeactivateOptions,
  ): Promise<VideoCallPictureInPictureStatus>;

  updateExecutorVideoCallPictureInPictureCamera(
    options: ExecutorVideoCallPictureInPictureCameraOptions,
  ): Promise<VideoCallPictureInPictureStatus>;

  updateVideoCallPictureInPictureSnapshot(
    options: VideoCallPictureInPictureSnapshotOptions,
  ): Promise<VideoCallPictureInPictureStatus>;

  activateVideoCallBackgroundCamera(
    options: VideoCallBackgroundCameraOptions,
  ): Promise<VideoCallPictureInPictureStatus>;

  deactivateVideoCallBackgroundCamera(): Promise<VideoCallPictureInPictureStatus>;

  updateVideoCallBackgroundCamera(
    options: VideoCallBackgroundCameraOptions,
  ): Promise<VideoCallPictureInPictureStatus>;

  syncOngoingCallNotification(options: OngoingCallNotificationOptions): Promise<void>;

  clearOngoingCallNotification(): Promise<void>;

  addListener(eventName: 'answer', listenerFunc: (order: Order) => void): Promise<PluginListenerHandle>;

  addListener(eventName: 'end', listenerFunc: (order: Order) => void): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'pictureInPictureChanged',
    listenerFunc: (state: VideoCallPictureInPictureState) => void,
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'executorVideoFrame',
    listenerFunc: (state: ExecutorVideoCallPictureInPictureFrame) => void,
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'executorVideoCallPictureInPictureDebug',
    listenerFunc: (state: ExecutorVideoCallPictureInPictureDebugEvent) => void,
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'videoCallBackgroundCameraFrame',
    listenerFunc: (state: VideoCallBackgroundCameraFrame) => void,
  ): Promise<PluginListenerHandle>;

  addListener(
    eventName: 'videoCallBackgroundCameraDebug',
    listenerFunc: (state: VideoCallBackgroundCameraDebugEvent) => void,
  ): Promise<PluginListenerHandle>;
}

const CallKit = registerPlugin<CallKitPlugin>('CallKit');

export default CallKit;

export interface Order {
  readonly id: number;
  readonly type?: number;
}

export interface OngoingCallNotificationOptions {
  readonly orderId: number;
  readonly title: string;
  readonly text: string;
  readonly endCallLabel: string;
  readonly isConnected: boolean;
  readonly hasLocalVideo: boolean;
  readonly startedAtMs?: number | null;
}

export interface OngoingCallNotificationPermissionStatus {
  readonly notifications: PermissionState;
}

export interface BackgroundCallModeOptions {
  readonly orderId: number;
}

export interface VideoCallPictureInPictureOptions {
  readonly orderId: number;
  readonly hasRemoteVideo: boolean;
  readonly aspectRatioWidth: number;
  readonly aspectRatioHeight: number;
  readonly preferSpeakerAudio?: boolean;
}

export interface VideoCallPictureInPictureDeactivateOptions {
  readonly finalizeAudioSession?: boolean;
}

export interface ExecutorVideoCallPictureInPictureOptions {
  readonly orderId: number;
  readonly aspectRatioWidth: number;
  readonly aspectRatioHeight: number;
  readonly cameraFacingMode: 'user' | 'environment';
  readonly preferSpeakerAudio?: boolean;
}

export interface VideoCallPictureInPictureStatus {
  readonly supported: boolean;
  readonly active: boolean;
  readonly reason?: string;
}

export interface ExecutorVideoCallPictureInPictureCameraOptions {
  readonly cameraFacingMode: 'user' | 'environment';
}

export interface VideoCallPictureInPictureSnapshotOptions {
  readonly orderId: number;
  readonly dataUrl: string;
}

export interface VideoCallBackgroundCameraOptions {
  readonly orderId: number;
  readonly cameraFacingMode: 'user' | 'environment';
}

export interface VideoCallPictureInPictureState {
  readonly active: boolean;
}

export interface ExecutorVideoCallPictureInPictureFrame {
  readonly dataUrl: string;
}

export interface VideoCallBackgroundCameraFrame {
  readonly dataUrl: string;
}

export interface ExecutorVideoCallPictureInPictureDebugEvent {
  readonly step: string;
  readonly reason?: string;
  readonly appState?: string;
  readonly active?: boolean;
  readonly possible?: boolean;
  readonly captureRunning?: boolean;
  readonly multitaskingSupported?: boolean;
  readonly multitaskingEnabled?: boolean;
  readonly attempts?: number;
  readonly cameraFacingMode?: 'user' | 'environment';
}

export interface VideoCallBackgroundCameraDebugEvent {
  readonly step: string;
  readonly reason?: string;
  readonly appState?: string;
  readonly captureRunning?: boolean;
  readonly multitaskingSupported?: boolean;
  readonly multitaskingEnabled?: boolean;
  readonly cameraFacingMode?: 'user' | 'environment';
}

export interface CallKitDebugLogOptions {
  readonly step: string;
  readonly data?: Record<string, string | number | boolean | null>;
}
